import {
  createContext,
  useContext,
  useState,
  useCallback,
  type ReactElement,
  type ReactNode,
} from 'react'

const MAX_OPEN_CHATS = 3

export interface ChatInstance {
  applicationId: string
  otherPartyName: string
  jobTitle: string
  minimized: boolean
  participantType: 'poster' | 'applicant'
}

interface ChatContextValue {
  openChats: ChatInstance[]
  inboxExpanded: boolean
  openChat: (params: Omit<ChatInstance, 'minimized'>) => void
  closeChat: (applicationId: string) => void
  minimizeChat: (applicationId: string) => void
  expandChat: (applicationId: string) => void
  toggleInbox: () => void
  closeInbox: () => void
}

const ChatContext = createContext<ChatContextValue | null>(null)

export function ChatProvider({
  children,
}: {
  children: ReactNode
}): ReactElement {
  const [openChats, setOpenChats] = useState<ChatInstance[]>([])
  const [inboxExpanded, setInboxExpanded] = useState(false)

  const openChat = useCallback((params: Omit<ChatInstance, 'minimized'>) => {
    setOpenChats((prev) => {
      // If already open, just expand it
      const existing = prev.find(
        (c) => c.applicationId === params.applicationId
      )
      if (existing) {
        return prev.map((c) =>
          c.applicationId === params.applicationId
            ? { ...c, minimized: false }
            : c
        )
      }

      // If at max, close the oldest to make room
      const updated = prev.length >= MAX_OPEN_CHATS ? prev.slice(1) : prev
      return [...updated, { ...params, minimized: false }]
    })
    // Close inbox when opening a chat
    setInboxExpanded(false)
  }, [])

  const closeChat = useCallback((applicationId: string) => {
    setOpenChats((prev) =>
      prev.filter((c) => c.applicationId !== applicationId)
    )
  }, [])

  const minimizeChat = useCallback((applicationId: string) => {
    setOpenChats((prev) =>
      prev.map((c) =>
        c.applicationId === applicationId ? { ...c, minimized: true } : c
      )
    )
  }, [])

  const expandChat = useCallback((applicationId: string) => {
    setOpenChats((prev) =>
      prev.map((c) =>
        c.applicationId === applicationId ? { ...c, minimized: false } : c
      )
    )
  }, [])

  const toggleInbox = useCallback(() => {
    setInboxExpanded((prev) => !prev)
  }, [])

  const closeInbox = useCallback(() => {
    setInboxExpanded(false)
  }, [])

  return (
    <ChatContext.Provider
      value={{
        openChats,
        inboxExpanded,
        openChat,
        closeChat,
        minimizeChat,
        expandChat,
        toggleInbox,
        closeInbox,
      }}
    >
      {children}
    </ChatContext.Provider>
  )
}

export function useChat(): ChatContextValue {
  const ctx = useContext(ChatContext)
  if (!ctx) {
    throw new Error('useChat must be used within a ChatProvider')
  }
  return ctx
}
